<template>
  <div class="review-comment-panel" :class="{ 'is-column': columnLayout }">
    <!-- Header -->
    <div class="panel-header">
      <div class="header-row">
        <div v-if="!columnLayout" class="header-name-version">
          <div class="version-field inline-editable">

            <!-- View mode: clickable tags -->
            <div v-if="!showVersionDropdown" class="version-field-row">
              <div class="clickable-field" @click="openVersionDropdown">
                <span class="tag is-info">
                  {{ isAdmin ? 'Internal' : '' }} {{ activeVersion.label }}
                </span>
                <span v-if="activeVersion.clientVersionNumber != null" class="tag is-link ml-2">
                  Client v{{ activeVersion.clientVersionNumber }}
                </span>
                <span v-if="versions.length > 1" class="icon edit-hint">
                  <font-awesome-icon :icon="['fal', 'edit']" />
                </span>
              </div>
              <template v-if="isAdmin && !isVersionUploadActive">
                <button
                  class="button is-small is-primary add-version-btn"
                  title="Upload new version"
                  @click="versionFileInputRef?.click()"
                >
                  <span class="icon is-small">
                    <font-awesome-icon :icon="['fal', 'plus']" />
                  </span>
                  <span>New version</span>
                </button>
              </template>
              <div
                v-if="isAdmin && isVersionUploadActive"
                class="inline-upload-progress"
              >
                <progress
                  class="progress is-small"
                  :class="versionUploadPaused ? 'is-warning' : 'is-primary'"
                  :value="isVersionUploadFinalizing ? undefined : versionUploadProgress"
                  max="100"
                >{{ versionUploadProgress }}%</progress>
                <span class="inline-upload-percent">{{ isVersionUploadFinalizing ? 'Finalizing…' : `${versionUploadProgress}%` }}</span>
                <button
                  v-if="!isVersionUploadFinalizing"
                  class="button is-small is-text inline-upload-toggle"
                  :title="versionUploadPaused ? 'Resume upload' : 'Pause upload'"
                  @click="toggleVersionUploadPause"
                >
                  <span class="icon is-small">
                    <font-awesome-icon :icon="['fas', versionUploadPaused ? 'play' : 'pause']" />
                  </span>
                </button>
              </div>
              <input
                ref="versionFileInputRef"
                type="file"
                class="is-hidden"
                accept="video/*,image/*,audio/*,.pdf"
                @change="onVersionFileSelected"
              />
            </div>
            <!-- Edit mode: SearchableSelect dropdown + close -->
            <div v-else class="is-flex is-align-items-center" style="position: relative; z-index: 100;">
              <SearchableSelect
                ref="versionSelectRef"
                :modelValue="activeVersion.id"
                :items="versions"
                value-field="id"
                display-field="label"
                placeholder="Select version..."
                :hide-search="true"
                class="version-inline-select"
                style="flex: 1; min-width: 200px;"
                @update:modelValue="onVersionSelectAndClose"
              >
                <template #item="{ item }">
                  <div class="is-flex is-align-items-center is-justify-content-space-between">
                    <span class="is-flex is-align-items-center" style="gap: 0.35rem">
                      {{ item.label }}
                      <span v-if="item.clientVersionNumber != null" class="tag is-link is-small">
                        Client v{{ item.clientVersionNumber }}
                      </span>
                    </span>
                    <span class="is-size-7 has-text-grey">{{ item.date }}</span>
                  </div>
                </template>
              </SearchableSelect>
              <button class="button is-small ml-1" @click="showVersionDropdown = false">
                <span class="icon">
                  <font-awesome-icon icon="fa-solid fa-xmark" />
                </span>
              </button>
            </div>
          </div>
          <input
            v-if="isRenaming"
            ref="renameInputRef"
            v-model="renameDraft"
            class="input title is-4 mb-0 rename-input"
            type="text"
            @keyup.enter="commitRename"
            @keyup.esc="isRenaming = false"
            @blur="commitRename"
          >
          <p v-else class="title is-4 mb-0 item-title-wrap" :title="itemName">
            {{ itemName }}
            <button
              v-if="isAdmin"
              class="button is-small rename-btn"
              title="Rename"
              @click="startRename"
            >
              <span class="icon is-small">
                <font-awesome-icon :icon="['fal', 'pen-to-square']" />
              </span>
            </button>
          </p>
          <p v-if="episodeDescription" class="is-size-7 has-text-grey mt-1 episode-description-text">{{ episodeDescription }}</p>
        </div>
        <div class="header-right">
          <span
            v-if="!isAdmin && requiresFinalization && finalizationStatus !== FINALIZATION_STATUS.COMPLETE"
            class="tag is-warning is-small mb-1"
            title="This asset requires finalization after your approval. You'll review the final version before it's saved to your library."
          >
            <span class="icon is-small mr-1">
              <font-awesome-icon :icon="['fal', 'wand-magic-sparkles']" />
            </span>
            Requires finalization
          </span>
          <div class="buttons mb-0">
            <template v-if="isApproved">
              <span class="tag is-success">
                <span class="icon is-small mr-1">
                  <font-awesome-icon :icon="['fas', 'check']" />
                </span>
                {{ statusType === 'internal' ? 'Internally Approved' : 'Approved' }}
              </span>
              <button
                v-if="isAdmin && stage !== 'client_review'"
                class="button is-small is-warning"
                @click="$emit('unapprove')"
              >
                <span class="icon is-small">
                  <font-awesome-icon :icon="['fal', 'rotate-left']" />
                </span>
                <span>Unapprove</span>
              </button>
            </template>
            <template v-else-if="showActionButtons">
              <!-- Shown only when the action is available, rather than disabled. -->
              <button
                v-if="!approveDisabledReason"
                class="button is-success is-small"
                @click="$emit('approve')"
              >
                <span class="icon is-small">
                  <font-awesome-icon :icon="['fal', 'check']" />
                </span>
                <span>{{ isFinalizationComplete ? 'Add to Library' : 'Approve' }}</span>
              </button>
              <button
                v-if="isAdmin"
                class="button is-warning is-small"
                @click="$emit('request-changes')"
              >
                <span class="icon is-small">
                  <font-awesome-icon :icon="['fal', 'rotate-left']" />
                </span>
                <span>Needs Revisions</span>
              </button>
              <button
                v-if="!isAdmin && !isFinalizationComplete && hasUnresolvedComments"
                class="button is-warning is-small"
                :class="{ 'is-active-status': itemStatus === REVIEW_ITEM_STATUS.CHANGES_REQUESTED }"
                @click="$emit('request-changes')"
              >
                <span class="icon is-small">
                  <font-awesome-icon :icon="['fal', 'pen-to-square']" />
                </span>
                <span>{{ requestChangesLabel }}</span>
              </button>
            </template>
          </div>
        </div>
      </div>
      <div class="header-sub-row">
        <slot name="versionRow"></slot>
      </div>
      <!-- Admin: Finalization toggle -->
      <div v-if="isAdmin && !isApproved" class="finalization-toggle">
        <div class="is-flex is-align-items-center" style="gap: 0.5rem;">
          <span class="is-size-7 has-text-grey">Requires finalization</span>
          <label class="switch is-small is-rounded">
            <input
              type="checkbox"
              :checked="localRequiresFinalization"
              @change="onFinalizationCheckboxChange"
            />
            <span class="slider"></span>
          </label>
        </div>
        <input
          v-if="localRequiresFinalization"
          class="input is-small mt-2"
          type="text"
          placeholder="Finalization notes (e.g. Burn in captions)..."
          :value="finalizationNote || ''"
          @input="emit('update:finalizationNote', $event.target.value || null)"
        />
      </div>

      <!-- Admin: Finalization needed banner -->
      <div v-if="isAdmin && finalizationStatus === FINALIZATION_STATUS.PENDING" class="notification is-warning is-size-7 finalization-banner mb-0">
        <span class="icon is-small mr-1">
          <font-awesome-icon :icon="['fal', 'wand-magic-sparkles']" />
        </span>
        <strong>Finalization needed</strong><span v-if="finalizationNote">: {{ finalizationNote }}</span>
      </div>

    </div>

    <!-- Finalization complete banner -->
    <div v-if="isFinalizationComplete && !isApproved" class="notification is-info is-size-7 finalization-complete-banner mb-0">
      <span class="icon is-small mr-1">
        <font-awesome-icon :icon="['fal', 'circle-check']" />
      </span>
      This asset has been finalized. Review it above and add it to your library.
    </div>

    <!-- Composer card: admin sees it only during internal_review; clients see it when not approved -->
    <div v-if="!isApproved && !isFinalizationComplete && (isAdmin ? isInternalReview : true)" class="composer-card">
      <textarea
        ref="textareaRef"
        class="composer-textarea"
        rows="2"
        placeholder="Add revision request..."
        :value="newCommentText"
        @input="$emit('update:newCommentText', $event.target.value)"
        @keydown.meta.enter="submitIfValid"
        @keydown.ctrl.enter="submitIfValid"
        @focus="onComposerFocus"
      ></textarea>
      <div class="composer-toolbar">
        <div class="composer-toolbar-left">
          <TimestampToolbar
            :itemType="itemType"
            :currentTime="currentTime"
            :capturedTimestamp="capturedTimestamp"
            :capturedTimestampEnd="capturedTimestampEnd"
            @set-start="$emit('set-start-timestamp')"
            @set-end="$emit('set-end-timestamp')"
            @clear="$emit('clear-timestamps')"
          />
        </div>
        <button
          class="button is-small is-primary"
          :disabled="!newCommentText?.trim()"
          @click="submitIfValid"
        >
          <span class="icon is-small">
            <font-awesome-icon :icon="['fal', 'plus']" />
          </span>
          <span>Add Comment</span>
        </button>
      </div>
    </div>

    <!-- Submit for review CTA (revision_queue stage) -->
    <div class="revision-actions-wrapper">
      <div v-if="isRevisionQueue && !isApproved && !allRevisionsAddressed && !hasUnresolvedComments && (hasNewVersion || !itemNeedsRevision)" class="revision-action-card">
        <span class="icon is-medium has-text-success">
          <font-awesome-icon :icon="['fal', 'paper-plane']" />
        </span>
        <p>Submit all revised items for another review</p>
        <button class="button is-success" @click="$emit('submit-for-review')">
          Submit for Review
        </button>
      </div>
    </div>

    <!-- Comments heading -->
    <div v-if="!isApproved" class="comments-heading">
      <span class="has-text-weight-semibold">Comments</span>
      <span class="tag is-rounded is-small is-primary">{{ comments.length }}</span>
      <span v-if="(isAdmin || allowAllVersions) && versions.length > 1" class="show-all-versions-switch">
        <input
          id="showAllVersionsSwitch"
          type="checkbox"
          class="switch is-rounded is-small is-primary"
          :checked="showAllVersions"
          @change="emit('update:showAllVersions', $event.target.checked)"
        />
        <label
          for="showAllVersionsSwitch"
          :title="showAllVersions ? 'Showing comments from all versions' : 'Showing comments from this version only'"
        >Show all versions</label>
      </span>
      <span v-if="showAllVersions" class="comment-sort-control">
        <span class="sort-label">Sort</span>
        <div class="buttons has-addons">
          <button
            type="button"
            class="button is-small"
            :class="{ 'is-primary': sortMode === 'timecode' }"
            title="Sort by timecode"
            @click="sortMode = 'timecode'"
          >Timecode</button>
          <button
            type="button"
            class="button is-small"
            :class="{ 'is-primary': sortMode === 'recent' }"
            title="Sort by most recent"
            @click="sortMode = 'recent'"
          >Recent</button>
        </div>
      </span>
    </div>

    <!-- Comment List -->
    <div v-if="!isApproved" class="comment-list" ref="commentListRef">
      <!-- Empty State -->
      <div v-if="comments.length === 0" class="empty-state">
        <span class="icon is-large has-text-grey-lighter">
          <font-awesome-icon :icon="['fal', 'comments']" size="2x" />
        </span>
        <p class="has-text-grey is-size-7 mt-2">No comments yet</p>
      </div>

      <!-- Threaded Comments -->
      <div
        v-for="thread in threadedComments"
        :key="thread.id"
        class="comment-thread"
        :style="commentStyle(thread)"
      >
        <!-- Top-level comment -->
        <div
          class="comment-item"
          :class="{ 'is-active': thread.id === activeCommentId }"
          @click="$emit('comment-click', thread)"
        >
          <div class="comment-header">
            <!-- Avatar -->
            <div class="avatar-circle" :style="{ background: thread.avatarColor }">
              <BunnyImage :fallback="thread.avatarImage" :alt="thread.author" img-class="avatar-img">
                <template #placeholder>{{ thread.avatarInitials }}</template>
              </BunnyImage>
            </div>

            <div class="comment-meta">
              <div class="comment-name-row">
                <span class="has-text-weight-semibold comment-author">
                  {{ thread.author }}
                </span>
                <span
                  v-if="thread.versionLabel"
                  class="tag is-small past-version-tag"
                  :title="`Comment from ${thread.versionLabel}`"
                >
                  {{ thread.versionLabel }}
                </span>
                <button
                  v-if="thread.timestamp != null"
                  class="timestamp-badge"
                  @click.stop="$emit('comment-click', thread)"
                >
                  <span class="icon is-small">
                    <font-awesome-icon :icon="['fal', 'clock']" />
                  </span>
                  {{ formatTimestamp(thread.timestamp) }}
                  <template v-if="thread.timestampEnd != null">
                    - {{ formatTimestamp(thread.timestampEnd) }}
                  </template>
                </button>
              </div>
            </div>

            <!-- Actions -->
            <div class="comment-actions">
              <button
                class="action-btn"
                title="Reply"
                @click.stop="startReplyMode(thread)"
              >
                <span class="icon is-small">
                  <font-awesome-icon :icon="['fal', 'reply']" />
                </span>
              </button>
              <button
                v-if="thread.isOwn"
                class="action-btn"
                title="Edit"
                @click.stop="startEditing(thread)"
              >
                <span class="icon is-small">
                  <font-awesome-icon :icon="['fal', 'pen']" />
                </span>
              </button>
              <button
                v-if="thread.isOwn"
                class="action-btn is-danger-action"
                title="Delete"
                @click.stop="$emit('delete-comment', thread)"
              >
                <span class="icon is-small">
                  <font-awesome-icon :icon="['fal', 'trash']" />
                </span>
              </button>
              <span class="has-text-grey comment-time">{{ thread.createdAt }}</span>
              <button
                v-if="isRevisionQueue"
                class="resolve-btn"
                :class="{ 'is-resolved': thread.resolved }"
                :title="thread.resolved ? 'Mark unresolved' : 'Mark resolved'"
                @click.stop="$emit('toggle-resolved', thread)"
              >
                <font-awesome-icon :icon="['fal', 'circle']" :active-icon="['fas', 'circle-check']" :active="thread.resolved" />
              </button>
            </div>
          </div>

          <!-- Edit mode -->
          <div v-if="editingCommentId === thread.id" class="comment-edit">
            <textarea
              ref="editTextareaRef"
              class="textarea is-small"
              rows="2"
              :value="editText"
              @input="editText = $event.target.value"
              @keydown.meta.enter="saveEdit(thread)"
              @keydown.ctrl.enter="saveEdit(thread)"
              @keydown.escape="cancelEdit"
            ></textarea>
            <div class="edit-actions">
              <button class="button is-small" @click="cancelEdit">Cancel</button>
              <button
                class="button is-small is-primary"
                :disabled="!editText?.trim()"
                @click="saveEdit(thread)"
              >
                Save
              </button>
            </div>
          </div>
          <p v-else class="comment-text">{{ thread.text }}</p>

        </div>

        <!-- Replies -->
        <div v-if="thread.replies.length > 0 || replyingToCommentId === thread.id" class="replies-list">
          <div
            v-for="reply in thread.replies"
            :key="reply.id"
            class="reply-item"
          >
            <div class="comment-header">
              <div class="avatar-circle avatar-circle-sm" :style="{ background: reply.avatarColor }">
                <BunnyImage :fallback="reply.avatarImage" :alt="reply.author" img-class="avatar-img">
                  <template #placeholder>{{ reply.avatarInitials }}</template>
                </BunnyImage>
              </div>
              <div class="comment-meta">
                <span class="has-text-weight-semibold reply-author">{{ reply.author }}</span>
              </div>
              <div class="comment-actions">
                <button
                  class="action-btn"
                  title="Reply"
                  @click.stop="startReplyMode(thread)"
                >
                  <span class="icon is-small">
                    <font-awesome-icon :icon="['fal', 'reply']" />
                  </span>
                </button>
                <button
                  v-if="reply.isOwn"
                  class="action-btn"
                  title="Edit"
                  @click.stop="startEditing(reply)"
                >
                  <span class="icon is-small">
                    <font-awesome-icon :icon="['fal', 'pen']" />
                  </span>
                </button>
                <button
                  v-if="reply.isOwn"
                  class="action-btn is-danger-action"
                  title="Delete"
                  @click.stop="$emit('delete-comment', reply)"
                >
                  <span class="icon is-small">
                    <font-awesome-icon :icon="['fal', 'trash']" />
                  </span>
                </button>
                <span class="has-text-grey comment-time">{{ reply.createdAt }}</span>
              </div>
            </div>

            <!-- Reply edit mode -->
            <div v-if="editingCommentId === reply.id" class="comment-edit reply-edit">
              <textarea
                ref="editTextareaRef"
                class="textarea is-small"
                rows="2"
                :value="editText"
                @input="editText = $event.target.value"
                @keydown.meta.enter="saveEdit(reply)"
                @keydown.ctrl.enter="saveEdit(reply)"
                @keydown.escape="cancelEdit"
              ></textarea>
              <div class="edit-actions">
                <button class="button is-small" @click="cancelEdit">Cancel</button>
                <button
                  class="button is-small is-primary"
                  :disabled="!editText?.trim()"
                  @click="saveEdit(reply)"
                >
                  Save
                </button>
              </div>
            </div>
            <p v-else class="comment-text reply-text">{{ reply.text }}</p>
          </div>

          <!-- Inline reply composer -->
          <div v-if="replyingToCommentId === thread.id" class="inline-reply-composer">
            <textarea
              ref="replyTextareaRef"
              class="textarea is-small"
              rows="2"
              placeholder="Write a reply..."
              :value="replyText"
              @input="replyText = $event.target.value"
              @keydown.meta.enter="submitReply"
              @keydown.ctrl.enter="submitReply"
              @keydown.escape="cancelReply"
            ></textarea>
            <div class="reply-composer-actions">
              <button class="button is-small" @click="cancelReply">Cancel</button>
              <button
                class="button is-small is-primary"
                :disabled="!replyText?.trim()"
                @click="submitReply"
              >
                Reply
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue'
import SearchableSelect from '@/components/common/SearchableSelect.vue'
import BunnyImage from '@/components/common/BunnyImage.vue'
import TimestampToolbar from '@/components/client/review/TimestampToolbar.vue'
import { formatTimestamp, commentStyle, REVIEW_ITEM_STATUS, FINALIZATION_STATUS } from '@/utils/reviewUtils'
import { useCommentThreads } from '@/composables/useCommentThreads'

const props = defineProps({
  comments: {
    type: Array,
    default: () => [],
  },
  itemType: {
    type: String,
    default: 'video',
  },
  itemName: {
    type: String,
    default: '',
  },
  requestChangesLabel: {
    type: String,
    default: 'Submit Revisions',
  },
  itemStatus: {
    type: String,
    default: REVIEW_ITEM_STATUS.PENDING_REVIEW,
  },
  hasUnresolvedComments: {
    type: Boolean,
    default: false,
  },
  isComposing: {
    type: Boolean,
    default: false,
  },
  newCommentText: {
    type: String,
    default: '',
  },
  currentTime: {
    type: Number,
    default: 0,
  },
  capturedTimestamp: {
    type: Number,
    default: null,
  },
  capturedTimestampEnd: {
    type: Number,
    default: null,
  },
  activeCommentId: {
    type: String,
    default: null,
  },
  versions: {
    type: Array,
    default: () => [],
  },
  activeVersion: {
    type: Object,
    default: () => ({ id: 'v1', label: 'v1' }),
  },
  versionUploading: {
    type: Boolean,
    default: false,
  },
  versionReplacing: {
    type: Boolean,
    default: false,
  },
  versionUploadProgress: {
    type: Number,
    default: 0,
  },
  versionUploadPaused: {
    type: Boolean,
    default: false,
  },
  statusType: {
    type: String,
    default: 'client',
  },
  isAdmin: {
    type: Boolean,
    default: false,
  },
  showAllVersions: {
    type: Boolean,
    default: false,
  },
  // Opt-in: lets non-admin (client) parents surface the "show all versions"
  // toggle. Admins always get it via isAdmin.
  allowAllVersions: {
    type: Boolean,
    default: false,
  },
  // Side-column mode (admin review tab): the parent owns the title/version
  // header; comments come first, composer + decision buttons pin to the bottom.
  columnLayout: {
    type: Boolean,
    default: false,
  },
  stage: {
    type: String,
    default: '',
  },
  allRevisionsAddressed: {
    type: Boolean,
    default: false,
  },
  hasNewVersion: {
    type: Boolean,
    default: false,
  },
  itemNeedsRevision: {
    type: Boolean,
    default: false,
  },
  requiresFinalization: {
    type: Boolean,
    default: false,
  },
  finalizationNote: {
    type: String,
    default: null,
  },
  finalizationStatus: {
    type: String,
    default: null,
  },
  episodeDescription: {
    type: String,
    default: '',
  },
})

const isApproved = computed(() => props.itemStatus === REVIEW_ITEM_STATUS.APPROVED)
const isInternalReview = computed(() => props.stage === 'internal_review')
const isRevisionQueue = computed(() => props.stage === 'revision_queue')
const isFinalizationComplete = computed(() => !props.isAdmin && props.requiresFinalization && props.finalizationStatus === FINALIZATION_STATUS.COMPLETE)
const isVersionUploadActive = computed(() => props.versionUploading || props.versionReplacing)
// Every byte sent ≠ done: Bunny still has to acknowledge the file and the
// version write follows. Show that wait honestly instead of a frozen 100%.
const isVersionUploadFinalizing = computed(() => isVersionUploadActive.value && props.versionUploadProgress >= 100)
const showActionButtons = computed(() => {
  if (isApproved.value) return false
  // Admin only sees action buttons during internal_review
  if (props.isAdmin) return isInternalReview.value
  return true
})

// Unsent draft text is feedback the client hasn't submitted — approving would
// silently discard it, so block approval until the box is submitted or cleared.
const approveDisabledReason = computed(() => {
  if (!props.isAdmin && props.newCommentText?.trim()) return 'Submit or clear your comment before approving'
  if (props.hasUnresolvedComments && !isFinalizationComplete.value) return 'Assets cannot be approved with submitted comments'
  return ''
})

const emit = defineEmits([
  'rename',
  'comment-click',
  'toggle-resolved',
  'start-composing',
  'set-start-timestamp',
  'set-end-timestamp',
  'clear-timestamps',
  'update:newCommentText',
  'submit-comment',
  'submit-reply',
  'version-change',
  'edit-comment',
  'delete-comment',
  'approve',
  'request-changes',
  'unapprove',
  'version-file-selected',
  'submit-for-review',
  'update:requiresFinalization',
  'update:finalizationNote',
  'pause-version-upload',
  'resume-version-upload',
  'update:showAllVersions',
])

// Inline rename of the item title (admin only). Writes item.name, which is also
// what becomes the library asset name on approval (functions/reviews/index.js).
const isRenaming = ref(false)
const renameDraft = ref('')
const renameInputRef = ref(null)

const startRename = async () => {
  renameDraft.value = props.itemName || ''
  isRenaming.value = true
  await nextTick()
  renameInputRef.value?.focus()
  renameInputRef.value?.select()
}

const commitRename = () => {
  if (!isRenaming.value) return
  isRenaming.value = false
  const name = renameDraft.value.trim()
  if (!name || name === props.itemName) return
  emit('rename', name)
}

function toggleVersionUploadPause() {
  if (props.versionUploadPaused) {
    emit('resume-version-upload')
  } else {
    emit('pause-version-upload')
  }
}

const textareaRef = ref(null)
const editTextareaRef = ref(null)
const versionSelectRef = ref(null)
const versionFileInputRef = ref(null)
const commentListRef = ref(null)
const editingCommentId = ref(null)
const editText = ref('')

function onVersionFileSelected(event) {
  const file = event.target.files?.[0]
  event.target.value = ''
  if (file) {
    emit('version-file-selected', file)
  }
}

// Local finalization state for immediate UI feedback (avoids prop round-trip delay)
const localRequiresFinalization = ref(props.requiresFinalization)
watch(() => props.requiresFinalization, (val) => { localRequiresFinalization.value = val })

function onFinalizationCheckboxChange(event) {
  const checked = event.target.checked
  localRequiresFinalization.value = checked
  emit('update:requiresFinalization', checked)
}

// Comment sort: 'timecode' (default) | 'recent'. Surfaced only in the
// all-versions view, where ordering by creation time helps reviewers see which
// feedback arrived for the latest version. Reset when all-versions turns off so
// the toggle never lingers hidden-but-active.
const sortMode = ref('timecode')
watch(() => props.showAllVersions, (on) => {
  if (!on) sortMode.value = 'timecode'
})

// Shared threading + reply logic
const {
  replyingToCommentId,
  replyText,
  replyTextareaRef,
  startReplyMode,
  cancelReply,
  submitReply: _submitReply,
  threadedComments,
} = useCommentThreads({
  comments: computed(() => props.comments),
  itemType: computed(() => props.itemType),
  activeCommentId: computed(() => props.activeCommentId),
  commentListRef,
  sortMode,
})

function submitReply() {
  _submitReply(emit)
}

// Version dropdown state
const showVersionDropdown = ref(false)

function openVersionDropdown() {
  if (props.versions.length <= 1) return
  showVersionDropdown.value = true
}

function onVersionSelectAndClose(versionId) {
  onVersionSelect(versionId)
  showVersionDropdown.value = false
}

function onComposerFocus() {
  emit('start-composing')
}

function onVersionSelect(versionId) {
  const version = props.versions.find(v => v.id === versionId)
  if (version) {
    emit('version-change', version)
  }
}

async function startEditing(comment) {
  editingCommentId.value = comment.id
  editText.value = comment.text
  await nextTick()
  if (editTextareaRef.value) {
    const el = Array.isArray(editTextareaRef.value) ? editTextareaRef.value[0] : editTextareaRef.value
    el?.focus()
  }
}

function cancelEdit() {
  editingCommentId.value = null
  editText.value = ''
}

function saveEdit(comment) {
  if (!editText.value?.trim()) return
  emit('edit-comment', { ...comment, text: editText.value.trim() })
  cancelEdit()
}

function submitIfValid() {
  if (props.newCommentText?.trim()) {
    emit('submit-comment')
  }
}

// Close version dropdown when item changes
watch(() => props.activeVersion?.id, () => {
  showVersionDropdown.value = false
})

</script>

<style lang="scss" scoped>
.review-comment-panel {
  display: flex;
  flex-direction: column;
  background: var(--bulma-scheme-main, #fff);
  flex: 1;
  min-height: 0;
}

.comments-heading {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem 0.25rem;

  .show-all-versions-switch {
    margin-left: auto;
    display: inline-flex;
    align-items: center;

    label {
      font-size: 0.75rem;
      color: var(--bulma-grey, #7a7a7a);
      cursor: pointer;
    }
  }

  // Sort toggle sits next to the all-versions switch (both grouped right via
  // the switch's margin-left:auto + the heading flex gap).
  .comment-sort-control {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;

    .sort-label {
      font-size: 0.75rem;
      color: var(--bulma-grey, #7a7a7a);
    }

    .buttons {
      margin-bottom: 0;

      .button {
        margin-bottom: 0;
        height: auto;
        padding: 0.2rem 0.55rem;
        font-size: 0.7rem;
      }
    }
  }
}

.past-version-tag {
  margin-left: 0.35rem;
  font-size: 0.7rem;
}

.header-row {
  justify-content: space-between;
}

.header-name-version {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.25rem;
  min-width: 0;
  flex: 1;
}

.item-title-wrap {
  word-break: break-word;
  overflow-wrap: anywhere;

  .rename-btn {
    vertical-align: middle;
    margin-left: 0.375rem;
    border: none;
    background: transparent;
    color: var(--bulma-text-weak, #6b7280);
    opacity: 0;
    transition: opacity 0.15s ease;
  }

  &:hover .rename-btn,
  .rename-btn:focus-visible {
    opacity: 1;
  }
}

.rename-input {
  height: auto;
}

.episode-description-text {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.4;
}

.header-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.35rem;
  flex-shrink: 0;
}

@media screen and (max-width: 768px) {
  .header-row {
    flex-direction: column;
    align-items: stretch;
    gap: 0.5rem;
  }

  .header-right {
    flex-direction: row;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.35rem;

    .buttons {
      flex-wrap: wrap;
      gap: 0.35rem;
    }
  }
}

.header-sub-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 0.35rem;
}

.version-field {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.inline-editable {
  position: relative;
  overflow: visible !important;

  .version-field-row {
    display: flex;
    align-items: center;
  }

  .clickable-field {
    cursor: pointer;
    padding: 0.25rem;
    border-radius: 4px;
    display: flex;
    align-items: center;

    &:hover {
      .edit-hint {
        opacity: 1;
      }
    }
  }

  .edit-hint {
    color: var(--bulma-text-light);
    font-size: 0.875rem;
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  .add-version-btn {
    margin-left: 0.5rem;
  }
}

.version-inline-select {
  :deep(.dropdown) {
    position: static !important;
  }

  :deep(.dropdown-menu) {
    position: absolute !important;
    bottom: 100% !important;
    top: auto !important;
    left: 0 !important;
    right: auto !important;
    min-width: 250px !important;
    z-index: 9999 !important;
    margin-bottom: 0.5rem !important;
  }
}


.inline-upload-progress {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: 0.5rem;
  min-width: 180px;

  .progress {
    flex: 1;
    margin-bottom: 0;
    height: 0.5rem;
    min-width: 80px;
  }

  .inline-upload-percent {
    font-size: 0.75rem;
    color: var(--bulma-text-weak);
    min-width: 2.5rem;
    text-align: right;
  }

  .inline-upload-toggle {
    padding: 0.25rem 0.5rem;
  }
}

.gap-2 {
  gap: 0.5rem;
}

.resolve-btn {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1.15rem;
  padding: 4px;
  margin-left: 2px;
  color: var(--bulma-grey-light, #b5b5b5);
  transition: color 0.15s ease, transform 0.1s ease;

  &:hover {
    color: var(--bulma-success, #48c78e);
    transform: scale(1.1);
  }

  &.is-resolved {
    color: var(--bulma-success, #48c78e);
  }
}

.comment-edit {
  margin-top: 0.35rem;
  margin-left: 40px;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.reply-edit {
  margin-left: 30px;
}

.edit-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.35rem;
}

.finalization-toggle {
  padding: 0.5rem 1rem 0.5rem 0;
  border-bottom: 1px solid var(--bulma-border, #eee);
}

// Column mode: flatten the header wrappers so every block can be reordered
// as a direct flex child of the panel.
.review-comment-panel.is-column {
  > .panel-header,
  > .panel-header > .header-row {
    display: contents;
  }

  .header-sub-row,
  .finalization-banner,
  .finalization-complete-banner { order: 0; margin: 0.5rem 1rem 0; }
  .comments-heading {
    order: 1;
    padding: 0.75rem 1rem 0.25rem;
    flex-wrap: wrap;
    row-gap: 0.4rem;

    .show-all-versions-switch label { white-space: nowrap; }

    // Narrow column: sort gets its own row instead of squeezing the toggle
    .comment-sort-control {
      flex-basis: 100%;
      justify-content: flex-end;

      .buttons { flex-wrap: nowrap; }
    }
  }
  .comment-list { order: 2; }
  .revision-actions-wrapper { order: 3; }
  .finalization-toggle {
    order: 4;
    padding: 0.5rem 1rem;
    border-bottom: none;
    border-top: 1px solid var(--bulma-border, #eee);
  }
  .composer-card { order: 5; margin: 0.5rem 1rem; }

  .header-right {
    order: 6;
    align-items: stretch;
    padding: 0.5rem 1rem 0.75rem;

    .buttons {
      flex-wrap: nowrap;

      .button,
      .tag {
        flex: 1;
        justify-content: center;
      }
    }
  }
}

// Stacked under the player (narrow modal): act first, read history after
@media screen and (max-width: 1399px) {
  .review-comment-panel.is-column {
    .composer-card { order: 1; }
    .header-right { order: 2; padding-top: 0; }
    .finalization-toggle { order: 3; border-top: none; }
    .revision-actions-wrapper { order: 4; }
    .comments-heading { order: 5; border-top: 1px solid var(--bulma-border, #eee); }
    .comment-list { order: 6; overflow-y: visible; }
  }
}

// Toggle switch
.switch {
  position: relative;
  display: inline-flex;
  align-items: center;
  cursor: pointer;

  input {
    opacity: 0;
    width: 0;
    height: 0;
    position: absolute;
  }

  .slider {
    width: 34px;
    height: 18px;
    background: var(--bulma-grey-lighter, #dbdbdb);
    border-radius: 18px;
    position: relative;
    transition: background 0.2s ease;

    &::before {
      content: '';
      position: absolute;
      width: 14px;
      height: 14px;
      left: 2px;
      top: 2px;
      background: #fff;
      border-radius: 50%;
      transition: transform 0.2s ease;
    }
  }

  input:checked + .slider {
    background: var(--bulma-primary, #485fc7);

    &::before {
      transform: translateX(16px);
    }
  }
}

.finalization-banner {
  margin: 0.5rem 1rem 0;
  padding: 0.5rem 0.75rem;
  display: flex;
  align-items: center;
}

.finalization-complete-banner {
  margin: 0.5rem 1rem 0;
  padding: 0.5rem 0.75rem;
  display: flex;
  align-items: center;
}

.revision-action-card {
  text-align: center;
  padding: 1.5rem;
  margin: 0.75rem;
  background: var(--bulma-scheme-main-bis);
  border-radius: 8px;
  border: 1px dashed var(--bulma-border);
  flex-shrink: 0;

  .icon {
    font-size: 1.5rem;
    margin-bottom: 0.5rem;
  }

  p {
    margin-bottom: 0.75rem;
    font-size: 0.875rem;
    color: var(--bulma-text-weak);
  }
}

</style>
